import { internalLogger } from '../../../interface/v1/clientOsLogger';
import {
  GetKeyValueOptions,
  JWebWindow,
  PutKeyValueOption,
  ValueStoreError,
  ValueStoreErrorType,
  ValueStoreGetResult,
  ValueStorePlugin
} from '../types';
import { jWebStates } from '../Initializer';
import * as JWebEnums from '../JWebEnums';
import { JWebCoreFacade } from './JWebCoreFacade';
import { loggerDecorator } from '../../../interface/v1/clientOsLogger/decorators';

/**
 * Facade for ValueStore operations (Native and Web)
 */
export class ValueStoreFacade {
  /**
   * A promise that resolves to true if the ValueStore is ready, otherwise false.
   */
  private static async isValueStoreReady(): Promise<boolean> {
    // Is Jweb ready?
    await jWebStates.JWebLibrary;

    // Is the plugins registered?
    await jWebStates.defaultPluginsRegistered;

    // Was valueStore installed?
    await jWebStates.plugins.ValueStore;
    return true;
  }

  /**
   * Get the ValueStore plugin
   */
  private static async getValueStorePlugin(): Promise<
    ValueStorePlugin | undefined
  > {
    // TODO: We should add a timeout here to avoid waiting indefinitely?

    await ValueStoreFacade.isValueStoreReady();

    const windowValueStore: ValueStorePlugin = (window as JWebWindow)?.JWeb
      ?.Plugins?.ValueStore;
    if (!windowValueStore) {
      internalLogger?.error(
        'Unable to obtain ValueStore (missing some JWeb plugin)'
      );
    }
    return windowValueStore;
  }

  /**
   * Write data to ValueStore
   */
  public static async writeDataToValueStore(
    entriesValue: Array<PutKeyValueOption>
  ): Promise<void> {
    try {
      // request current valueStore.
      const valueStore = await this.getValueStorePlugin();

      if (valueStore) {
        await new Promise<void>((resolve) => {
          // TODO: check this code
          valueStore.put({
            entries: entriesValue
          });
          resolve();
        });
      }
    } catch (e) {
      internalLogger?.error(
        'JWeb error: Unable to write in Value Store. JWeb failed.'
      );
    }
  }

  /**
   * Retrieves the values for a given array of keys from the Jarvis Value Store
   */
  @loggerDecorator.debug.d3()
  public static async get(
    options: GetKeyValueOptions
  ): Promise<ValueStoreGetResult | ValueStoreError> {
    const valueStore = await this.getValueStorePlugin();
    if (valueStore) {
      try {
        const result = await valueStore.get(options);
        return result;
      } catch (error) {
        internalLogger?.error('JWeb error: ValueStore get error', error);
        return {
          errorType: ValueStoreErrorType.unknownError,
          errorReason: 'JWeb error: ValueStore.get() operation failed.'
        };
      }
    }
  }

  /**
   * Write a key-value pair to ValueStore
   */
  public static async writeKeyValueToValueStore(
    key: string,
    value: string
  ): Promise<void> {
    const valueStore = await this.getValueStorePlugin();
    if (valueStore) {
      await new Promise<void>((resolve) => {
        valueStore.put({
          entries: [{ key, value }]
        });
        resolve();
      });
    }
  }

  /**
   * Write Stratus user ID to ValueStore
   */
  @loggerDecorator.debug.d3()
  private static async writeStratusUserIdToValueStore(
    entryValue: string
  ): Promise<void> {
    await this.writeKeyValueToValueStore(
      JWebEnums.ReservedValueStoreKeys.stratusUserId,
      entryValue
    );
  }

  /**
   * Write tenant ID to ValueStore
   */
  @loggerDecorator.debug.d3()
  private static async writeTenantIdToValueStore(
    entryValue: string
  ): Promise<void> {
    await this.writeKeyValueToValueStore(
      JWebEnums.ReservedValueStoreKeys.tenantId,
      entryValue
    );
  }

  /**
   * Write WPID to ValueStore
   */
  @loggerDecorator.debug.d3()
  private static async writeWpidToValueStore(
    entryValue: string
  ): Promise<void> {
    await this.writeKeyValueToValueStore(
      JWebEnums.ReservedValueStoreKeys.wpIdUserId,
      entryValue
    );
  }

  /**
   * Write HP ID user ID to ValueStore
   */
  @loggerDecorator.debug.d3()
  private static async writeHpIdUserIdValueStore(
    entryValue: string
  ): Promise<void> {
    await this.writeKeyValueToValueStore(
      JWebEnums.ReservedValueStoreKeys.hpIdUserId,
      entryValue
    );
  }

  @loggerDecorator.debug.d3()
  public static async writeStratusCookieDataToValueStore(
    stratusId: string,
    tenantId: string,
    wpIdUserId: string,
    hpIdUserId: string
  ): Promise<void> {
    if (!(await JWebCoreFacade.isNative())) {
      await this.writeStratusUserIdToValueStore(stratusId);
      await this.writeTenantIdToValueStore(tenantId);
      await this.writeWpidToValueStore(wpIdUserId);
      await this.writeHpIdUserIdValueStore(hpIdUserId);
    }
  }
}
